import { useState, useCallback, useMemo, useEffect } from 'react';
import type { NumberSize, ResizeCallback } from 're-resizable';
import useResizeAware from 'react-resize-aware';
import { atom, useRecoilState } from 'recoil';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import { reporterRightDrawerState } from 'domains/reporter/Reporter/state';

type Dimension = 'width' | 'height';

/**
 * Grouped resize callbacks for use with the Resizable component.
 * Spread this object onto the Resizable component to ensure all callbacks are connected:
 * `<Resizable {...resizableCallbacks} size={...} />`
 */
export type ResizableCallbacks = {
  onResizeStart: () => void;
  onResize: ResizeCallback;
  onResizeStop: ResizeCallback;
};

export type UseResizableReturn = {
  isResizing: boolean;
  /**
   * Grouped callbacks for the Resizable component. Spread onto Resizable to ensure
   * all three callbacks (onResizeStart, onResize, onResizeStop) are connected.
   */
  resizableCallbacks: ResizableCallbacks;
  componentSize: number;
  setComponentSize: (arg1: number) => void;
  openComponent: boolean;
  setOpenComponent: (arg1: boolean) => void;
  toggleOpenComponent: () => void;
  componentLastExpandedSize: number;
  resizeListener: React.ReactNode;
  sizes: Nullable<{
    width: number;
    height: number;
  }>;
  showCollapseWarning: boolean;
};

export type UseResizableProps = {
  localStoragePersisterKey?: string;
  collapseSize: number;
  expandSize?: number;
  minimumSize: number;
  dimension: Dimension;
  initialSize: number;
  initialOpen?: boolean;
  expandToLastSize?: boolean;
};

type ComponentState = {
  size: number;
  open: boolean;
  shouldCollapse: boolean;
  lastExpandedSize: number;
};

type PersistedResizableState = {
  sidebarSize: number;
  sidebarLastExpandedSize: number;
  isSidebarOpen: boolean;
};

const fallbackAtomsByPersisterKey = new Map<
  string,
  ReturnType<typeof atom<PersistedResizableState>>
>();

const createPersistedDefaultState = (
  initialSize: number,
  initialOpen: boolean
): PersistedResizableState => ({
  sidebarSize: initialSize,
  sidebarLastExpandedSize: initialSize,
  isSidebarOpen: initialOpen,
});

const createPersistedFallbackAtom = (
  initialSize: number,
  initialOpen: boolean,
  localStoragePersisterKey: string
) => {
  const cachedAtom = fallbackAtomsByPersisterKey.get(localStoragePersisterKey);
  if (cachedAtom != null) {
    return cachedAtom;
  }

  const keyedFallbackAtom = atom<PersistedResizableState>({
    key: `fallback${localStoragePersisterKey}`,
    default: createPersistedDefaultState(initialSize, initialOpen),
    effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
  });

  fallbackAtomsByPersisterKey.set(localStoragePersisterKey, keyedFallbackAtom);
  return keyedFallbackAtom;
};

type UseResizableCoreStateParams = {
  collapseSize: number;
  expandSize: number;
  minimumSize: number;
  dimension: Dimension;
  expandToLastSize: boolean;
  initialState: ComponentState;
  onComponentStateChange?: (state: ComponentState) => void;
};

const useResizableCoreState = ({
  collapseSize,
  expandSize,
  minimumSize,
  dimension,
  expandToLastSize,
  initialState,
  onComponentStateChange,
}: UseResizableCoreStateParams): UseResizableReturn => {
  const [componentState, setComponentState] = useState<ComponentState>(initialState);
  const [isResizing, setIsResizing] = useState(false);

  const updateComponentState = useCallback(
    (updater: (previousState: ComponentState) => ComponentState) => {
      setComponentState((prev) => {
        return updater(prev);
      });
    },
    []
  );

  useEffect(() => {
    onComponentStateChange?.(componentState);
  }, [componentState, onComponentStateChange]);

  const handleComponentResizeStart = useCallback(() => {
    updateComponentState((prev) => {
      return { ...prev, shouldCollapse: false };
    });
    setIsResizing(true);
  }, [updateComponentState]);

  const [resizeListener, sizes] = useResizeAware();

  const {
    size: componentSize,
    open: openComponent,
    lastExpandedSize: componentLastExpandedSize,
  } = componentState;
  const showCollapseWarning = openComponent && sizes != null && sizes[dimension] < collapseSize;

  const handleComponentResizeStop = useCallback(
    (event: MouseEvent | TouchEvent, direction: string, ref: HTMLElement, delta: NumberSize) => {
      setIsResizing(false);

      updateComponentState((prev: ComponentState) => {
        const newState = {
          ...prev,
        };
        if (prev.open === false) {
          newState.size = Math.max(collapseSize, prev.size + delta[dimension]);
          newState.open = true;
          newState.lastExpandedSize = newState.size;
        } else if (prev.shouldCollapse === true) {
          newState.open = false;
          newState.shouldCollapse = false;
          newState.size = minimumSize;
          newState.lastExpandedSize = expandSize;
        } else {
          newState.size = prev.size + delta[dimension];
          if (newState.size > minimumSize) {
            newState.lastExpandedSize = newState.size;
          }
        }

        return newState;
      });
    },
    [collapseSize, expandSize, minimumSize, dimension, updateComponentState]
  );

  const handleComponentResize = useCallback(
    (event: MouseEvent | TouchEvent, direction: string, ref: HTMLElement, delta: NumberSize) => {
      updateComponentState((prev: ComponentState) => {
        return {
          ...prev,
          shouldCollapse: componentSize + delta[dimension] <= collapseSize,
        };
      });
    },
    [componentSize, collapseSize, dimension, updateComponentState]
  );

  const toggleOpenComponent = useCallback(() => {
    updateComponentState((prev: ComponentState) => {
      const isOpening = prev.open === false;
      const newComponentSize = isOpening
        ? expandToLastSize
          ? prev.lastExpandedSize
          : expandSize
        : minimumSize;
      const newState: ComponentState = {
        ...prev,
        size: newComponentSize,
        open: !prev.open,
        lastExpandedSize: isOpening ? newComponentSize : prev.lastExpandedSize,
      };

      return newState;
    });
  }, [expandSize, expandToLastSize, minimumSize, updateComponentState]);

  const setComponentSize = useCallback(
    (size: number) => {
      updateComponentState((prev: ComponentState) => {
        const newState = {
          ...prev,
          size,
          lastExpandedSize: size > collapseSize ? size : prev.lastExpandedSize,
        };

        return newState;
      });
    },
    [collapseSize, updateComponentState]
  );

  const setOpenComponent = useCallback(
    (open: boolean) => {
      updateComponentState((prev: ComponentState) => {
        const newState = {
          ...prev,
          open,
        };

        return newState;
      });
    },
    [updateComponentState]
  );

  const resizableCallbacks: ResizableCallbacks = useMemo(
    () => ({
      onResizeStart: handleComponentResizeStart,
      onResize: handleComponentResize,
      onResizeStop: handleComponentResizeStop,
    }),
    [handleComponentResizeStart, handleComponentResize, handleComponentResizeStop]
  );

  return {
    isResizing,
    resizableCallbacks,
    componentSize,
    setComponentSize,
    openComponent,
    setOpenComponent,
    toggleOpenComponent,
    componentLastExpandedSize,
    resizeListener,
    sizes,
    showCollapseWarning,
  };
};

export const useResizableWithPersistence = ({
  localStoragePersisterKey,
  collapseSize,
  expandSize,
  minimumSize,
  dimension,
  initialSize,
  initialOpen,
  expandToLastSize,
}: UseResizableProps & {
  localStoragePersisterKey: string;
}): UseResizableReturn => {
  const resolvedExpandSize = expandSize ?? collapseSize;
  const resolvedInitialOpen = initialOpen ?? initialSize > minimumSize;
  const resolvedExpandToLastSize = expandToLastSize ?? false;

  const persistenceAtom = useMemo(() => {
    // reporterRightDrawerState is a global atom
    if (localStoragePersisterKey === 'reporterRightDrawerState') {
      return reporterRightDrawerState;
    }

    return createPersistedFallbackAtom(initialSize, resolvedInitialOpen, localStoragePersisterKey);
  }, [resolvedInitialOpen, initialSize, localStoragePersisterKey]);

  const [persistedState, setPersistedState] = useRecoilState(persistenceAtom);

  const handleComponentStateChange = useCallback(
    (nextState: ComponentState) => {
      setPersistedState((prev) => {
        if (
          prev.sidebarSize === nextState.size &&
          prev.sidebarLastExpandedSize === nextState.lastExpandedSize &&
          prev.isSidebarOpen === nextState.open
        ) {
          return prev;
        }

        return {
          ...prev,
          sidebarSize: nextState.size,
          sidebarLastExpandedSize: nextState.lastExpandedSize,
          isSidebarOpen: nextState.open,
        };
      });
    },
    [setPersistedState]
  );

  const initialComponentState = useMemo<ComponentState>(() => {
    return {
      size: persistedState.sidebarSize,
      open: persistedState.isSidebarOpen,
      shouldCollapse: false,
      lastExpandedSize: persistedState.sidebarLastExpandedSize,
    };
  }, [persistedState]);

  return useResizableCoreState({
    collapseSize,
    expandSize: resolvedExpandSize,
    minimumSize,
    dimension,
    expandToLastSize: resolvedExpandToLastSize,
    initialState: initialComponentState,
    onComponentStateChange: handleComponentStateChange,
  });
};

export const useResizable = ({
  collapseSize,
  expandSize,
  minimumSize,
  dimension,
  initialSize,
  initialOpen,
  expandToLastSize,
}: Omit<UseResizableProps, 'localStoragePersisterKey'>): UseResizableReturn => {
  const resolvedExpandSize = expandSize ?? collapseSize;
  const resolvedInitialOpen = initialOpen ?? initialSize > minimumSize;
  const resolvedExpandToLastSize = expandToLastSize ?? false;

  return useResizableCoreState({
    collapseSize,
    expandSize: resolvedExpandSize,
    minimumSize,
    dimension,
    expandToLastSize: resolvedExpandToLastSize,
    initialState: {
      size: initialSize,
      open: resolvedInitialOpen,
      shouldCollapse: false,
      lastExpandedSize: initialSize,
    },
  });
};
